import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from "react-i18next";
import '../pages.css';
import {
  MButton, LoadingContext, M_StateActions,
  MHeader, MTransferList, NavigateContext
} from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from '../../components/utils/apiResponse';
import DynamicForm from '../../components/Forms/DynamicForm';

// =======================================================================================================================================================
// Helpers and Rules
// =======================================================================================================================================================

//=======================================================================================================================================================
// Converts a string to camelCase (used to build DTO payload keys from Option.Header/Name).
//=======================================================================================================================================================
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

export default function CarrierConfigCrud({ handleTabClose, toastMessages }) {
  const dispatch = useDispatch();
  const ctx = useContext(LoadingContext);
  const M_State = useSelector((state) => state.StateKeyValue);
  const { t, i18n } = useTranslation();
  const { renameTab } = useContext(NavigateContext);
  const selectedTab = M_State.find(o => o.key === 'selectedTab')?.value;

  const globalOperation = M_State.find(obj => obj.key === 'operation')?.value;
  const globalLine = M_State.find(obj => obj.key === 'CarrierLine')?.value;

  //=======================================================================================================================================================
  // Per-tab state (kept isolated by selectedTab id key)
  //======================================================================================
  const [id, setId] = useState(M_State.find(obj => obj.key === 'selectedTab')?.value);
  const [operation, setOperation] = useState(globalOperation ?? 'Error');
  const [sessionLine, setSessionLine] = useState(globalLine ?? {});
  const [carrierInfo, setCarrierInfo] = useState({});
  const [isFirst, setIsFirst] = useState(true);
  const [carrierId, setCarrierId] = useState(sessionLine?.Id);

  // Options for DynamicForm (no FK fetching here; DynamicForm handles it)
  const [allOptions, setAllOptions] = useState([]);      // Raw Options for DynamicForm

  // Imperative ref to DynamicForm (submit/getValues/reset)
  const formRef = useRef(null);

  // Stale-closure guards
  const idRef = useRef(id);
  const operationRef = useRef(operation);
  const carrierIdRef = useRef(carrierId);
  useEffect(() => { idRef.current = id; }, [id]);
  useEffect(() => { operationRef.current = operation; }, [operation]);
  useEffect(() => { carrierIdRef.current = carrierId; }, [carrierId]);

  // Async race guard between tab switches
  const tabTokenRef = useRef(0);
  useEffect(() => { tabTokenRef.current += 1; }, [id]);

  const isViewMode = (operation || '').toLowerCase() === 'view';
  const isUpdateMode = (operation || '').toLowerCase() === 'edit';

  // BufferServices (explicit baseUrl to avoid hooks inside constructor)
  const svcCarriers = useMemo(() => new BufferService('Carriers', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcCreate = useMemo(() => new BufferService('Carriers/Create', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcUpdate = useMemo(() => new BufferService('Carriers/Update', ctx.DEFAULTURL), [ctx.DEFAULTURL]);

  // View mode: extract items for transfer list display
  const transferViewItems = Array.isArray(carrierInfo?.CarrierTypeOfServices)
    ? carrierInfo.CarrierTypeOfServices
    : [];

  //=====================================================================================================================================================================
  // EFFECT
  // Update tab title on operation or entity name change
  //=====================================================================================================================================================================
  useEffect(() => {
    if (!selectedTab || !operation) return;

    // Determine the i18n key (label) based on the operation
    const i18nKey =
      operation === 'Create' ? 'LabelNewCarrier' :
        operation === 'Edit' ? 'LabelEditCarrier' :
          'LabelViewCarrier';

    // Get the name from sessionLine first, then carrierInfo as fallback
    const name = sessionLine?.Name ?? carrierInfo?.Name ?? '';
    const base = t(i18nKey);
    const label = (operation === 'Create' || !name) ? base : `${base} "${name}"`;
    renameTab(String(selectedTab), label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, operation, sessionLine?.Name, carrierInfo?.Name]);

  //=======================================================================================================================================================
  // fetchOptions
  // Fetches raw Options for Carriers and prepares FK datasets for DynamicForm.
  // Steps:
  // 1) Call /Options for Carriers and store raw options.
  // 2) Optionally fetch entity details for Edit/View.
  // NOTE: FK lists are NOT fetched here; DynamicForm handles FK sources internally.
  //=======================================================================================================================================================
  const fetchOptions = useCallback(async (op, line, { skipDetails } = { skipDetails: false }) => {
    const myToken = tabTokenRef.current;
    ctx.setIsLoad(true);
    try {
      const res = await svcCarriers.options(); // /api/Options for Carriers
      if (isFailurePayload(res) || res?.name === 'AxiosError') {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        ctx.setIsLoad(false);
        return;
      }

      if (myToken !== tabTokenRef.current) return; // drop late updates after tab switch
      setAllOptions(res || []);

      const mode = op || operationRef.current || 'View';
      if (!skipDetails && mode !== 'Create' && (line?.Id || sessionLine?.Id)) {
        await fetchDetails(line?.Id || sessionLine?.Id);
      }
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, svcCarriers, sessionLine?.Id, t, toastMessages]);

  //=====================================================================================================================================================================
  // Fetch entity details (Edit/View) and keep raw record; DynamicForm hydrates UI state.
  //=====================================================================================================================================================================
  const fetchDetails = useCallback(async (idToGet) => {
    const myToken = tabTokenRef.current;
    ctx.setIsLoad(true);
    try {
      const res = await svcCarriers.getId(idToGet);
      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        return;
      }
      if (myToken !== tabTokenRef.current) return;
      setCarrierInfo(res || {});
      setCarrierId(res?.Id);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
    }
  }, [ctx, svcCarriers, t, toastMessages]);

  ///=======================================================================================================================================================
  // getSessionStorageData
  // Restores a tab snapshot if available; otherwise fetches options and
  // details as needed for the current mode.
  // Steps:
  // 1) Try to load a persisted snapshot for this tab id.
  // 2) If found, restore options and entity state; else refetch options,
  //    details for Edit/View, or transfer dataset for Create.
  //=======================================================================================================================================================
  const getSessionStorageData = useCallback((sessionID) => {
    ctx.setIsLoad(true);
    const data = M_State.find(obj => obj.key === sessionID)?.value;
    if (data) {
      const pick = (k) => data.find((x) => x.key === k)?.value;
      const tempCI = pick('carrierInfo');
      const tempIF = pick('isFirst');
      const tempOP = pick('operation');
      const tempSL = pick('sessionLine');
      const tempOptions = pick('allOptions') ?? pick('formSchema'); // backward compatibility
      const tempCarrierId = pick('carrierId');

      setCarrierInfo(tempCI || {});
      setIsFirst(tempIF ?? false);
      setOperation(tempOP ?? 'Error');
      setSessionLine(tempSL || {});
      if (tempSL?.Id) setCarrierId(tempSL.Id);
      else if (tempCarrierId) setCarrierId(tempCarrierId);
      if (Array.isArray(tempOptions)) setAllOptions(tempOptions);

      ctx.setIsLoad(false);
      return;
    }

    // No snapshot : refetch
    fetchOptions(operationRef.current, sessionLine, { skipDetails: true }).finally(async () => {
      if (operationRef.current !== 'Create' && sessionLine?.Id) {
        await fetchDetails(sessionLine.Id);
      }
      ctx.setIsLoad(false);
    });
  }, [M_State, ctx, fetchOptions, fetchDetails, sessionLine]);

  //=====================================================================================================================================================================
  // EFFECT
  // Initialize state from session storage on first render
  //=====================================================================================================================================================================
  useEffect(() => {
    // Restore session data for this tab
    const sessionForThisTab = M_State.find(o => o.key === id)?.value || [];
    const pick = (k) => sessionForThisTab.find(x => x.key === k)?.value;

    const sessionOp = pick('operation');
    const sessionSL = pick('sessionLine');

    // restore create form state so it does not get wiped on remount
    const sessionCI = pick('carrierInfo');
    const sessionIF = pick('isFirst');
    const sessionOptions = pick('allOptions') ?? pick('formSchema'); // backward compatibility
    const sessionCarrierId = pick('carrierId');

    // Check for session data (globals win)
    const seedOperation = (globalOperation !== undefined ? globalOperation : sessionOp);
    const seedLine = (globalLine !== undefined ? globalLine : sessionSL);

    if (seedOperation !== undefined) setOperation(seedOperation);
    if (seedLine) {
      setSessionLine(seedLine);
      if (seedLine.Id) setCarrierId(seedLine.Id);
    } else if (sessionCarrierId) {
      setCarrierId(sessionCarrierId);
    }

    if (sessionCI) setCarrierInfo(sessionCI);
    if (sessionIF !== undefined) setIsFirst(sessionIF);
    else if (isFirst) setIsFirst(false);
    if (Array.isArray(sessionOptions)) setAllOptions(sessionOptions);

    // consume one-shot globals
    if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: 'operation' }));
    if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: 'CarrierLine' }));

    // Fetch Options first; details as needed
    fetchOptions(seedOperation, seedLine, { skipDetails: true }).finally(async () => {
      if (seedOperation && seedOperation !== 'Create' && seedLine?.Id) {
        await fetchDetails(seedLine.Id);
        ctx.setIsLoad(false);
      } else {
        ctx.setIsLoad(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //=====================================================================================================================================================================
  // EFFECT
  // Persist per-tab snapshot
  //=====================================================================================================================================================================
  // Debounced snapshot to reduce churn
  const saveSnapshotTimer = useRef(null);
  useEffect(() => {
    if (!id) return;

    // Merge RHF current values so unsaved edits persist across tabs
    const currentFormValues = formRef.current?.getValues?.() || {};
    const infoToPersist =
      (operation || '').toLowerCase() === 'view'
        ? carrierInfo
        : { ...carrierInfo, ...currentFormValues };

    const sessionData = [
      { key: 'carrierInfo', value: infoToPersist },
      { key: 'isFirst', value: isFirst },
      { key: 'operation', value: operation },
      { key: 'sessionLine', value: sessionLine },
      { key: 'allOptions', value: allOptions },
      // Also store entity id to avoid leaking across tabs
      { key: 'carrierId', value: carrierId },
    ];

    if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
    saveSnapshotTimer.current = setTimeout(() => {
      dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
    }, 80);

    return () => {
      if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
    };
  }, [id, carrierInfo, isFirst, operation, sessionLine, allOptions, carrierId, dispatch]);

  //=====================================================================================================================================================================
  // EFFECT
  // Track tab switch -->> restore that tab snapshot or refetch
  //=====================================================================================================================================================================
  useEffect(() => {
    const newId = M_State.find(obj => obj.key === 'selectedTab')?.value;
    if (newId && newId !== id) {
      setId(newId);
      getSessionStorageData(newId);
    }
  }, [M_State, id, getSessionStorageData]); // keep

  //=====================================================================================================================================================================
  // EFFECT
  // React to language changes: re-fetch Options so labels reflect current language
  //=====================================================================================================================================================================
  useEffect(() => {
    if (!operation || isFirst) return; // skip first render or no operation set
    fetchOptions(operation, sessionLine, { skipDetails: true });

    // Determine the i18n key (label) based on the operation
    const i18nKey =
      operation === 'Create' ? 'LabelNewCarrier' :
        operation === 'Edit' ? 'LabelEditCarrier' :
          'LabelViewCarrier';

    // Get the name from sessionLine first, then carrierInfo as fallback
    const name = sessionLine?.Name ?? carrierInfo?.Name ?? '';
    const base = t(i18nKey);
    const label = (operation === 'Create' || !name) ? base : `${base} "${name}"`;
    renameTab(String(selectedTab), label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  ///=======================================================================================================================================================
  // buildPayloadFromValues
  // Builds DTO payload from values emitted by DynamicForm and raw options.
  // Steps:
  // 1) Iterate options to determine the correct key source (Header for FK, Name otherwise).
  // 2) Coerce booleans; leave other scalars as-is. FK values already come as ids.
  // 3) Lower camel case keys for DTO and assemble object.
  //=======================================================================================================================================================
  const buildPayloadFromValues = useCallback((values) => {
    const options = Array.isArray(allOptions) ? allOptions : [];
    const entries = [];

    for (const opt of options) {
      if (!opt) continue;

      const keySource = opt.IsForeignKey ? (opt.Header || opt.Name) : opt.Name;
      const payloadKey = toCamel(opt.Header || keySource);

      let v;
      if (opt.Type === 'boolean' || opt.Type === 'bool') {
        v = !!values?.[opt.Name];
      } else {
        v = values?.[keySource];
      }

      if (v !== undefined) entries.push([payloadKey, v]);
    }

    return Object.fromEntries(entries);
  }, [allOptions]);

  //=======================================================================================================================================================
  // onSubmitDynamic
  // Receives values from DynamicForm and performs create/update flow.
  // Steps:
  // 1) Build DTO payload from form values.
  // 2) On Create, merge M:N relation from transfer list (ids only).
  // 3) Call create or update service.
  // 4) Toast result and close the tab.
  //=======================================================================================================================================================
  const onSubmitDynamic = useCallback(
    async (values) => {
      ctx.setIsLoad(true);
      try {
        const payload = buildPayloadFromValues(values);

        // Attach transfer list only on Create (DTO: carrierTypeOfServiceIds)
        if ((operationRef.current || '') === 'Create') {
          const raw = carrierInfo.carrierTypeOfServiceIds || [];
          const ids = raw.map(x => (typeof x === 'object' ? x.Id : x)).filter(v => v != null);
          payload.carrierTypeOfServiceIds = ids;
        }

        const op = (operationRef.current || '').toLowerCase();
        const currentId = carrierIdRef.current;

        const res =
          op === 'create'
            ? await svcCreate.post(payload)
            : await svcUpdate.update(currentId, payload);

        if (isFailurePayload(res)) {
          toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
          return;
        }
        toastMessages('success', '', op === 'create' ? t('CarrierCreated') : t('CarrierUpdated'));
        handleTabClose(idRef.current);
      } catch (err) {
        toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
      } finally {
        ctx.setIsLoad(false);
      }
    },
    [buildPayloadFromValues, ctx, handleTabClose, svcCreate, svcUpdate, t, toastMessages, carrierInfo]
  );

  //=======================================================================================================================================================
  // triggerFormSubmit
  // Triggers DynamicForm submit programmatically via imperative ref.
  //=======================================================================================================================================================
  const triggerFormSubmit = () => {
    formRef.current?.submit?.();
  };

  //=======================================================================================================================================================
  // onRefresh
  // Re-fetches options and details if applicable for current mode.
  // NOTE: No FK fetching here.
  //=======================================================================================================================================================
  const onRefresh = () => {
    const op = operationRef.current;
    fetchOptions(op, sessionLine);
  };

  //=======================================================================================================================================================
  // handleFormValuesChange
  // Receives small patches from DynamicForm and persists them in the
  // per-tab snapshot so edits survive tab switches.
  //=======================================================================================================================================================
  const handleFormValuesChange = useCallback(
    (patch, meta) => {
      if (!patch || !idRef.current) return;

      const prevTabState = M_State.find((o) => o.key === idRef.current)?.value || [];
      const pick = (k) => prevTabState.find((x) => x.key === k)?.value;
      const merged = { ...(pick('carrierInfo') || carrierInfo || {}), ...patch };

      const updated = [
        ...prevTabState.filter((x) => x.key !== 'carrierInfo'),
        { key: 'carrierInfo', value: merged }
      ];
      dispatch(M_StateActions.addM_State({ key: idRef.current, value: updated }));
    },
    [M_State, carrierInfo, dispatch]
  );

  //=====================================================================================================================================================================
  // View specific rendering for M:N relation
  //=====================================================================================================================================================================

  return (
    <>
      <MHeader element={
        <>
          <MButton
            tooltip={t("RefreshTable")}
            icon={<i className="pi pi-refresh iconTable"></i>}
            onClick={onRefresh}
            label={t("RefreshTable")}
          />
          {!isViewMode && (
            <MButton
              tooltip={t("Save")}
              icon={<i className="pi pi-save iconTable"></i>}
              onClick={triggerFormSubmit}
              label={t("Save")}
            />
          )}
        </>
      } />

      <div style={{
        padding: '20px',
        background: 'var(--color-white)'
      }}>
        <div className="firstDivContainer">

          <div className="carrier-config-layout">
            <div className="carrier-config-form">
              <DynamicForm
                key={`df-carrier-${id}-${operation}-${carrierId ?? 'new'}`}
                formRef={formRef}
                mode={(operation || 'View').toLowerCase()}
                schema={allOptions}
                data={carrierInfo}
                onSubmit={onSubmitDynamic}
                onValuesChange={handleFormValuesChange}
                title={t("CarrierInformation")}
                align="vertical"
              />
            </div>

            {!isUpdateMode && (
              <div className="carrier-config-transfer">
                <MTransferList
                  disabled={isViewMode}
                  header={t("CarrierTypeOfServices")}
                  entity={'CarrierTypeOfServices'}
                  field={'Name'}
                  valueIdField={'Id'}
                  value={isViewMode ? transferViewItems : (carrierInfo.carrierTypeOfServiceIds || [])}
                  setValue={(val) => {
                    if (isViewMode) return;
                    setCarrierInfo({ ...carrierInfo, carrierTypeOfServiceIds: val });
                  }}
                  multiple
                  height={isViewMode ? 400 : 440}
                />
              </div>

            )}
          </div>
        </div>

      </div>
    </>
  );
}

CarrierConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func
};
